import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslationApp } from "../../lib/i18next";
import {
  CAMPAIGN_APPROVAL_ENABLE_SUCCESS_DESCRIPTION,
  CONTENT_APPROVAL_ENABLE_SUCCESS_DESCRIPTION,
  APPROVAL_ENABLE_SUCCESS_OR_SEPARATOR,
  APPROVAL_REVIEWER_MAX_REACHED,
} from "../../i18n/keysTranslations";
import ContentApprovalReviewLink from "./ContentApprovalReviewLink";
import ApprovalReviewEmailSend from "./ApprovalReviewEmailSend";
import ApprovalAddReviewerButton from "./ApprovalAddReviewerButton";
import ApprovalReviewersList from "./ApprovalReviewersList";
import { MAX_APPROVAL_REVIEWERS } from "./approvalReviewersUtils";

const DESCRIPTION_BY_TYPE = {
  campaign: CAMPAIGN_APPROVAL_ENABLE_SUCCESS_DESCRIPTION,
  content: CONTENT_APPROVAL_ENABLE_SUCCESS_DESCRIPTION,
};

const createLocalReviewer = ({ reviewerID = "", url = "" } = {}) => ({
  localID: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  reviewerID,
  url,
  email: "",
  isEmailSent: false,
});

const ApprovalEnableSuccessContent = ({
  approvalType = "content",
  approvalUrl = "",
  approvalID = "",
  reviewerID = "",
  defaultEmail = "",
  onCopied = () => {},
}) => {
  const { t } = useTranslationApp();
  const descriptionKey =
    DESCRIPTION_BY_TYPE[approvalType] ||
    CONTENT_APPROVAL_ENABLE_SUCCESS_DESCRIPTION;

  const [isMultiReviewerMode, setIsMultiReviewerMode] = useState(false);
  const [reviewers, setReviewers] = useState(() => [
    createLocalReviewer({ reviewerID, url: approvalUrl }),
  ]);

  useEffect(() => {
    setReviewers([createLocalReviewer({ reviewerID, url: approvalUrl })]);
    setIsMultiReviewerMode(false);
  }, [approvalUrl, approvalID, approvalType, reviewerID]);

  const canAddReviewer = reviewers.length < MAX_APPROVAL_REVIEWERS;

  const onAddReviewer = () => {
    setIsMultiReviewerMode(true);
    setReviewers((currentReviewers) => {
      if (currentReviewers.length >= MAX_APPROVAL_REVIEWERS) {
        return currentReviewers;
      }

      if (currentReviewers.length === 1) {
        return [...currentReviewers, createLocalReviewer()];
      }

      return [...currentReviewers, createLocalReviewer()];
    });
  };

  const onPrimaryReviewerEmailSent = ({ email }) => {
    setReviewers((currentReviewers) =>
      currentReviewers.map((reviewer, index) =>
        index === 0 ? { ...reviewer, email, isEmailSent: true } : reviewer
      )
    );
  };

  const onReviewerAdded = ({
    localID,
    reviewerID: nextReviewerID,
    url,
    email,
  }) => {
    setReviewers((currentReviewers) =>
      currentReviewers.map((reviewer) =>
        reviewer.localID === localID
          ? {
              ...reviewer,
              reviewerID: nextReviewerID,
              url,
              email,
            }
          : reviewer
      )
    );
  };

  const classicLayout = (
    <>
      <ContentApprovalReviewLink
        approvalUrl={approvalUrl}
        onCopied={onCopied}
      />
      <Typography
        variant="body2"
        sx={{
          textAlign: "center",
          color: "text.secondary",
          my: 1.5,
          textTransform: "lowercase",
        }}
      >
        {t(APPROVAL_ENABLE_SUCCESS_OR_SEPARATOR)}
      </Typography>
      <ApprovalReviewEmailSend
        approvalType={approvalType}
        approvalID={approvalID}
        reviewerID={reviewers[0]?.reviewerID || reviewerID}
        defaultEmail={reviewers[0]?.email || defaultEmail}
        initialIsSent={reviewers[0]?.isEmailSent}
        onCallbackEmailSent={onPrimaryReviewerEmailSent}
      />
      {canAddReviewer ? (
        <ApprovalAddReviewerButton onClick={onAddReviewer} sx={{ mt: 2 }} />
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
          {t(APPROVAL_REVIEWER_MAX_REACHED)}
        </Typography>
      )}
    </>
  );

  return (
    <Box sx={{ textAlign: "left", px: 1 }}>
      <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1.5 }}>
        {t(descriptionKey)}
      </Typography>

      {isMultiReviewerMode ? (
        <ApprovalReviewersList
          reviewers={reviewers}
          approvalType={approvalType}
          approvalID={approvalID}
          onCopied={onCopied}
          onAddReviewer={onAddReviewer}
          onReviewerAdded={onReviewerAdded}
          onPrimaryReviewerEmailSent={onPrimaryReviewerEmailSent}
        />
      ) : (
        classicLayout
      )}
    </Box>
  );
};

export default ApprovalEnableSuccessContent;
